import styled from '@emotion/styled';
import { Box, CircularProgress, Grid, Paper, TextField, Tooltip, Typography } from '@mui/material';
import _ from 'lodash';
import { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { generatePath, useHistory, useParams } from 'react-router-dom';
import { useAnalytics } from 'use-analytics';
import { AutomatedInsightSessionType } from '../../modules/automated-insights/constants';
import {
  canUseAutomatedInsightSessions,
  isAutomatedInsightSessionShareLink,
} from '../../modules/automated-insights/helpers';
import { getUserCustomizations, getUserProfile } from '../../modules/user/selectors';
import { snackbar } from '../../notifications';
import { Paths } from '../../routes';
import { TrackEvent } from '../analytics';
import SessionAssetGrid from './SessionAssetGrid';
import { useCreateSessionGoal } from './hooks/useAutomatedInsightSessionGoal';
import useFetchSession from './hooks/useFetchSession';
import useGenerateInsights from './hooks/useGenerateInsights';
import CustomButton from './ui/Button';

const ExperienceGoalTextField = styled(TextField)(({ theme }) => ({
  '& .MuiInputBase-root': {
    padding: 0,
  },
  '& .MuiInputBase-input': {
    fontSize: '12px',
    padding: '8px 12px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  '& fieldset': { border: 'none' },
}));

function ExperienceGoalEditor({ onChange, value, disabled = false }) {
  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box py={1}>
        <Typography fontSize={12}>
          Please enter a description of the task you want your user to perform.
        </Typography>
      </Box>
      <Box
        sx={{
          flexGrow: 1,
          backgroundColor: '#FFFFFF',
          borderRadius: '16px',
          border: '1px solid rgba(217, 217, 217, 0.83)',
        }}>
        <ExperienceGoalTextField
          className="lightContainer"
          disabled={disabled}
          value={value}
          placeholder="Example: Vistors are looking to buy a fishing license."
          multiline
          minRows={7}
          onChange={(ev) => onChange(ev.target.value)}
          sx={{
            width: '100%',
            marginTop: 1,
            overflow: 'auto',
            fontSize: '12px',
            lineHeight: '20px',
          }}
        />
      </Box>
    </Box>
  );
}

export default function EditSessionSettings() {
  const { track } = useAnalytics();
  const history = useHistory();
  const user = useSelector(getUserProfile);
  const userCustomizations = useSelector(getUserCustomizations);

  const { sessionId } = useParams();

  const isShareLink = isAutomatedInsightSessionShareLink(sessionId);

  const hasAutomatedInsightsFeatureEnabled = canUseAutomatedInsightSessions(user, userCustomizations);

  const [goal, setGoal] = useState('');

  const { mutateAsync: createSessionGoalAsync } = useCreateSessionGoal();
  const { mutateAsync: generateInsightsAsync } = useGenerateInsights();

  const [isGeneratingInsights, setIsGeneratingInsights] = useState(false);

  const { data: sessionAndTasks } = useFetchSession(
    { sessionId }, // todo: include tests with personalized findings only...should be exceedingly rare
    {
      onSuccess: (data) => {
        // Can't edit session assets post-launch, which is the case if there are any tasks
        const alreadyLaunched = Object.values(data?.tasks).filter((task) => !!task.status)?.length > 0;
        const isExperience = data?.automatedInsightSession?.type === AutomatedInsightSessionType.Experience;

        if (alreadyLaunched || !isExperience) {
          history.replace({
            pathname: generatePath(Paths.automatedInsights.session, {
              sessionId,
            }),
          });
        }
        // redirect user to assets page if assets are not well formed
        const assets = data?.automatedInsightSession?.assets ?? [];
        const isPopulated = assets.length > 0 && assets.every((asset) => !!asset.image);

        if (!isPopulated) {
          history.replace({
            pathname: generatePath(Paths.automatedInsights.sessionAssets, {
              sessionId,
            }),
          });
        }
      },
      onError: (err) => {
        if ([403, 404].includes(err?.response?.status)) {
          history.replace({
            pathname: Paths.automatedInsights.basePath,
          });
        } else {
          snackbar.error(
            err?.response?.humanReadableMessage ??
              'Something went wrong getting this Pulse. Please try again later.'
          );
        }
      },
      retry: 1,
    }
  );

  const startGeneratingInsights = useCallback(async () => {
    setIsGeneratingInsights(true);

    let sessionGoal;

    try {
      sessionGoal = await createSessionGoalAsync({
        automatedInsightSessionId: sessionAndTasks.automatedInsightSession.id,
        description: goal,
        isSelected: true,
      });
    } catch (e) {
      snackbar.error('Something went wrong generating insights. Please try again later.');
      setIsGeneratingInsights(false);
      return;
    }

    // kick off a task for proposed audience
    try {
      await generateInsightsAsync({
        sessionId: sessionAndTasks.automatedInsightSession.id,
        goalId: sessionGoal.id,
      });
      track(TrackEvent.GENERATE_PULSE_WITH_GOAL_AND_PROPOSED_AUDIENCE, {
        sessionId,
      });
    } catch (e) {
      snackbar.error('Something went wrong generating insights. Please try again later.');
      setIsGeneratingInsights(false);
      return;
    }

    history.push({
      pathname: generatePath(Paths.automatedInsights.session, {
        sessionId: sessionAndTasks.automatedInsightSession.id,
      }),
      state: { sessionDetails: sessionAndTasks.automatedInsightSession },
    });
  }, [
    createSessionGoalAsync,
    generateInsightsAsync,
    goal,
    history,
    sessionAndTasks,
    sessionId,
    setIsGeneratingInsights,
    track,
  ]);

  if (!sessionAndTasks || ((_.isNil(user) || _.isNil(userCustomizations)) && !isShareLink)) {
    return (
      <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!isShareLink && !hasAutomatedInsightsFeatureEnabled) {
    return window.location.replace('/');
  }

  return (
    <Box mt={3}>
      <Grid container spacing={2} pt={3}>
        <Grid container item spacing={6}>
          <Grid item xs={12} md={6} pb={2}>
            <Paper elevation={0} sx={{ borderRadius: 4 }}>
              <Box
                sx={{ height: 'calc(100vh - 160px)', overflow: 'auto', borderRadius: 4 }}
                className="transparentScrollBar">
                <SessionAssetGrid
                  session={sessionAndTasks.automatedInsightSession}
                  baseHeight="(100vh - 200px)"
                  sx={{ height: 'inherit', borderRadius: 4 }}
                />
              </Box>
            </Paper>
          </Grid>
          <Grid item xs={12} md={6} pb={2}>
            <Paper elevation={0} sx={{ borderRadius: 4, height: 'calc(100vh - 160px)' }}>
              <Box
                className="transparentScrollBar"
                sx={{
                  height: '80%',
                  borderRadius: 4,
                }}
                px={2}
                pt={2}
                mb={1}>
                <Box p={2} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <Typography variant="body2" fontWeight="bold">
                    Task
                  </Typography>
                  <ExperienceGoalEditor
                    onChange={(value) => setGoal(value)}
                    value={goal}
                    disabled={isGeneratingInsights}
                  />
                </Box>
              </Box>
              <Box px={4}>
                <Grid container justifyContent="flex-end">
                  <Grid item>
                    <CustomButton
                      variant="secondaryDark"
                      sx={{ marginLeft: 1 }}
                      onClick={() => {
                        history.push({
                          pathname: generatePath(Paths.automatedInsights.sessionAssets, {
                            sessionId: sessionAndTasks.automatedInsightSession.id,
                          }),
                        });
                      }}
                      disabled={isGeneratingInsights}>
                      Back
                    </CustomButton>
                  </Grid>
                  <Grid item>
                    {isGeneratingInsights ? (
                      <Box px={2}>
                        <CircularProgress color="secondary" size="2rem" />
                      </Box>
                    ) : (
                      <Tooltip
                        title={
                          !goal
                            ? 'Please enter a task to start generating insights'
                            : 'Start generating insights'
                        }>
                        <span>
                          <CustomButton
                            variant="gradient"
                            sx={{ marginLeft: 1 }}
                            onClick={() => startGeneratingInsights()}
                            disabled={isGeneratingInsights || !goal}>
                            Next
                          </CustomButton>
                        </span>
                      </Tooltip>
                    )}
                  </Grid>
                </Grid>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
}
